import { apiService } from "@/services";
import { useMutation, useQuery, useInfiniteQuery } from "@tanstack/react-query";
import type { InfiniteData } from "@tanstack/react-query";
import { handleApiError } from "@/utils";
import queryClient from "@/query-client";

interface NotificationData {
  type: string;
  title: string;
  body: string;
  action_url?: string;
  category?: string;
  priority?: string;
  metadata?: { brand_id?: number } & Record<string, unknown>;
}

export interface Notification {
  id: string;
  type: string;
  notifiable_type: string;
  notifiable_id: number;
  data: NotificationData;
  read_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PaginatedNotifications {
  data: Notification[];
  current_page: number;
  last_page: number;
  total: number;
  per_page: number;
}

export interface GetNotificationsParams {
  unread?: boolean;
  page?: number;
  per_page?: number;
  brand_id?: number;
}

const NOTIFICATIONS_KEY = ["notifications"];
const UNREAD_COUNT_KEY = ["notifications", "unread-count"];

export { NOTIFICATIONS_KEY, UNREAD_COUNT_KEY };

export const useGetNotifications = (params: GetNotificationsParams = {}) =>
  useQuery<PaginatedNotifications>({
    queryKey: [...NOTIFICATIONS_KEY, "list", params],
    queryFn: () =>
      apiService.get<PaginatedNotifications, GetNotificationsParams>("/notifications", params),
    staleTime: 30000,
  });

export const useInfiniteNotifications = (params: Omit<GetNotificationsParams, "page"> = {}) =>
  useInfiniteQuery<PaginatedNotifications>({
    queryKey: [...NOTIFICATIONS_KEY, "infinite", params],
    queryFn: ({ pageParam = 1 }) =>
      apiService.get<PaginatedNotifications, GetNotificationsParams>("/notifications", {
        ...params,
        page: pageParam as number,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.current_page < lastPage.last_page ? lastPage.current_page + 1 : undefined,
    staleTime: 30000,
  });

export const useGetUnreadCount = () =>
  useQuery<{ count: number }>({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: () => apiService.get("/notifications/unread-count"),
    staleTime: 30000,
  });

/**
 * Shared by useMarkRead/useMarkAllRead's optimistic updates -- both patch
 * every cached notification page in place. The shape guard matters:
 * NOTIFICATIONS_KEY also matches useGetNotifications' flat (non-infinite)
 * cache entry, and spreading `pages` onto that shape would corrupt it.
 */
function patchNotificationsCache(updater: (n: Notification) => Notification) {
  queryClient.setQueriesData<InfiniteData<PaginatedNotifications>>({ queryKey: NOTIFICATIONS_KEY }, (old) => {
    if (!old || !("pages" in old) || !Array.isArray(old.pages)) return old;
    return {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        data: (page.data ?? []).map(updater),
      })),
    };
  });
}

export const useMarkRead = () =>
  useMutation<unknown, unknown, string>({
    mutationFn: (id) => apiService.post(`/notifications/${id}/read`),
    onMutate: (id) => {
      const now = new Date().toISOString();
      patchNotificationsCache((n) => (n.id === id ? { ...n, read_at: n.read_at ?? now } : n));

      queryClient.setQueryData<{ count: number }>(UNREAD_COUNT_KEY, (old) =>
        old ? { count: Math.max(0, old.count - 1) } : old,
      );
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      handleApiError(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });

export const useMarkAllRead = () =>
  useMutation<
    unknown,
    unknown,
    void,
    {
      notificationSnapshots: [readonly unknown[], PaginatedNotifications | undefined][];
      unreadSnapshot: { count: number } | undefined;
    }
  >({
    mutationFn: () => apiService.post("/notifications/read-all"),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_KEY });
      await queryClient.cancelQueries({ queryKey: UNREAD_COUNT_KEY });

      const notificationSnapshots = queryClient.getQueriesData<PaginatedNotifications>({
        queryKey: NOTIFICATIONS_KEY,
      });
      const unreadSnapshot = queryClient.getQueryData<{ count: number }>(UNREAD_COUNT_KEY);

      const now = new Date().toISOString();
      patchNotificationsCache((n) => (n.read_at === null ? { ...n, read_at: now } : n));

      queryClient.setQueryData<{ count: number }>(UNREAD_COUNT_KEY, { count: 0 });

      return { notificationSnapshots, unreadSnapshot };
    },
    onError: (error, _vars, context) => {
      if (context?.notificationSnapshots) {
        context.notificationSnapshots.forEach(([key, data]) =>
          queryClient.setQueryData(key as readonly unknown[], data)
        );
      }
      if (context?.unreadSnapshot !== undefined) {
        queryClient.setQueryData(UNREAD_COUNT_KEY, context.unreadSnapshot);
      }
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      handleApiError(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
